import { SUBMISSION_MAX_BYTES, SUBMISSION_MAX_FILES, SUBMISSION_MAX_FILE_BYTES, submissionPath } from './verification.js';
/**
 * Runs in the guest (also used by the author fixtures). Only regular, bounded files.
 *
 * Untracked files that git ignores are left behind, the way `node_modules` always
 * was. The grader's clean gate compares every file it receives with `HEAD`, and
 * the checkpoints' own workflows make ignored files as a matter of course — a
 * build into `var/`, a cache, a log, a `.env` — so an honest learner who had
 * followed the brief exactly failed `clean` and spent an attempt on it. What
 * travels is now what git itself calls the working tree: committed work, plus
 * anything untracked that nobody chose to ignore, which the gate still refuses.
 * It is also the only reason a learner's `.env` ever left the tab. Tracked files
 * are never ignored, so nothing committed can be hidden this way; ignoring an
 * untracked file only takes it away from the grader.
 */
export function submissionExportScript(missionId, outputFile) {
    return `const fs = require('node:fs');
const { execFileSync } = require('node:child_process');
const missionId = ${JSON.stringify(missionId)};
const accept = ${submissionPath.toString()};
const files = [];
let bytes = 0;
const ignored = new Set();
try {
  const listed = execFileSync('git', ['-C', 'storefront', 'ls-files', '-z', '--others', '--ignored', '--exclude-standard', '--directory'],
    {maxBuffer: 64 * 1024 * 1024, stdio: ['ignore', 'pipe', 'ignore']}).toString('utf8');
  for (const path of listed.split(String.fromCharCode(0))) if (path) ignored.add('storefront/' + path);
} catch { /* no repository to ask: the validator refuses the submission anyway */ }
function walk(dir) {
  if (!fs.lstatSync(dir).isDirectory()) throw Error('Submission contains a symbolic link');
  for (const entry of fs.readdirSync(dir, {withFileTypes: true}).sort((a,b) => a.name.localeCompare(b.name))) {
    const name = dir + '/' + entry.name;
    if (entry.name === 'node_modules') continue;
    if (ignored.has(name) || ignored.has(name + '/')) continue;
    if (entry.isDirectory()) {
      if (name === 'storefront/.git' || !name.includes('/.git/')) walk(name);
      else if (/^storefront\\/\\.git\\/(objects(?:\\/.*)?|refs(?:\\/(heads|tags|remotes)(?:\\/.*)?)?)$/.test(name)) walk(name);
    } else if (accept(name, missionId)) {
      if (!entry.isFile()) throw Error('Submission contains a symbolic link');
      const stat = fs.lstatSync(name);
      if (stat.size > ${SUBMISSION_MAX_FILE_BYTES}) throw Error('Submission file is too large');
      const data = fs.readFileSync(name).toString('base64');
      bytes += data.length + name.length + 64;
      if (bytes > ${SUBMISSION_MAX_BYTES - 1024} || files.length >= ${SUBMISSION_MAX_FILES}) throw Error('Submission is too large');
      files.push({path: name, data, executable: !!(stat.mode & 0o111)});
    }
  }
}
walk('storefront');
if (missionId === 'l19-ship-it') walk('origin.git');
const output = JSON.stringify({format:1, missionId, files}).replace(/[^\\x00-\\x7f]/g, (ch) => '\\\\u' + ch.charCodeAt(0).toString(16).padStart(4, '0'));
${outputFile ? `fs.writeFileSync(${JSON.stringify(outputFile)}, output, {flag: 'wx', mode: 0o600}); process.stdout.write(String(output.length));` : 'process.stdout.write(output);'}`;
}
//# sourceMappingURL=submission-export.js.map